import CommonMapper from '@/modules/core/mappers/CommonMapper'
import Insurance from '@/modules/common/common/models/APP/Insurance'
import BankDetails from '@/modules/common/hiring/models/APP/BankDetails'
import ProcessStatus from '@/modules/common/hiring/models/APP/ProcessStatus'
import PetsHiringAnswers from '@/modules/pets/hiring/models/APP/PetsHiringAnswers'
import type PetsGetRecoveryResponse from '@/modules/pets/hiring/models/MID/responses/PetsGetRecoveryResponse'
import type PetsInsurance from '@/modules/pets/common/models/APP/PetsInsurance'
import { PetHiring } from '../../models/APP/PetHiring'
import Insured from '@/modules/common/common/models/APP/Insured'
import { UserRoleEnum } from '@/modules/core/models/enums'
import DocumentDetails from '@/modules/common/hiring/models/APP/DocumentDetails'
import AddressDetails from '@/modules/common/hiring/models/APP/AddressDetails'
import UserDetails from '@/modules/common/hiring/models/APP/UserDetails'
import { EXTENSION_MIMETYPE } from '@/modules/common/common/constants/CommonConstants'
import { isPureBreed } from '../../utils'

export default class {
  static fromResponse(data: PetsGetRecoveryResponse, product: PetsInsurance): PetsHiringAnswers {
    const file = data.documents?.[0]
    const mimeType = file?.mimeType
      ? EXTENSION_MIMETYPE[file.mimeType.toLowerCase()]
      : 'application/pdf'
    return new PetsHiringAnswers({
      quotationId: data.quotationId,
      businessLine: data.hiring.businessLine,
      billingCycle: CommonMapper.fromDXPProductPrice(data.hiring.billingCycle),
      price: data.hiring.price,
      totalPrice: data.hiring.totalPrice,
      product: new Insurance({
        ...product,
        quotationNumber: String(data.hiring.quotationNumber),
        clientCode: String(data.hiring.clientCode),
        productCode: String(data.hiring.productCode)
      }),
      processStatus: new ProcessStatus({
        statusCode: data.hiring.statusCode,
        stepCode: data.hiring.stepCode
      }),
      startDate: data.hiring.insuranceStartDate
        ? CommonMapper.fromMiddlewareDate(data.hiring.insuranceStartDate)
        : undefined,
      bankDetails: new BankDetails({
        name: data.paymentMeans?.holderName,
        iban: data.paymentMeans?.iban
      }),
      civilLiabilityInsured: data.insureds?.[0]
        ? new Insured({
            documentDetails: new DocumentDetails({
              documentType: data.insureds[0].documentType,
              documentNumber: data.insureds[0].documentNumber,
              personType: data.insureds[0].personType,
              documentExpirationDate: data.insureds[0].documentExpirationDate
                ? CommonMapper.fromMiddlewareDate(data.insureds[0].documentExpirationDate)
                : undefined
            }),
            addressDetails: new AddressDetails({
              postalCode: data.insureds[0].postalCode,
              province: data.insureds[0].province,
              city: data.insureds[0].city,
              address: data.insureds[0].street,
              country: data.insureds[0].nationality ?? data.insureds[0].nacionality,
              houseType: data.insureds[0].houseType
                ? String(data.insureds[0].houseType)
                : undefined,
              gateway: data.insureds[0].gateway,
              stair: data.insureds[0].stair,
              floor: data.insureds[0].floor,
              doorway: data.insureds[0].doorway,
              addressExtra: data.insureds[0].restAddress,
              addressNormalize: data.insureds[0].addressNormalice,
              streetType: data.insureds[0].streetType,
              roadNumber: data.insureds[0].house
            }),
            userDetails: new UserDetails({
              birthDate: data.insureds[0].birthDate
                ? CommonMapper.fromMiddlewareDate(data.insureds[0].birthDate)
                : undefined,
              name: data.insureds[0].name?.toUpperCase(),
              surname1: data.insureds[0].firstSurname?.toUpperCase(),
              surname2: data.insureds[0].secondSurname?.toUpperCase(),
              gender: data.insureds[0].sex,
              email: data.insureds[0].email,
              prefix: data.insureds[0].prefix,
              phone: data.insureds[0].mobilePhone ? String(data.insureds[0].mobilePhone) : undefined
            })
          })
        : undefined,
      isLiabilityInsuredPolicyholder: data.insureds?.[0]?.isPolicyHolder,
      pets:
        data.insuredsPets?.map((p) => {
          const v = new PetHiring(p)
          if (v.breedCode) {
            v.pureBreed = String(isPureBreed(v.breedCode))
          }
          v.petBirthDate = v.petBirthDate
            ? CommonMapper.fromMiddlewareDate(v.petBirthDate as string)
            : undefined
          return v
        }) ?? [],
      policyHolder: new Insured({
        role: data.policyHolder.role ?? UserRoleEnum.HOLDER_INSURED,
        documentDetails: new DocumentDetails({
          documentType: data.policyHolder.documentType,
          documentNumber: data.policyHolder.documentNumber,
          personType: data.policyHolder.personType,
          documentExpirationDate: data.policyHolder.documentExpirationDate
            ? CommonMapper.fromMiddlewareDate(data.policyHolder.documentExpirationDate)
            : undefined,
          documentId: file?.documentId || file?.documentData,
          fileName: file?.fileName,
          mimeType: mimeType
        }),
        addressDetails: new AddressDetails({
          postalCode: data.policyHolder.postalCode,
          province: data.policyHolder.province,
          city: data.policyHolder.city,
          address: data.policyHolder.street,
          country: data.policyHolder.nationality,
          houseType: data.policyHolder.houseType ? String(data.policyHolder.houseType) : undefined,
          gateway: data.policyHolder.gateway,
          stair: data.policyHolder.stair,
          floor: data.policyHolder.floor,
          doorway: data.policyHolder.doorway,
          addressExtra: data.policyHolder.restAddress,
          streetType: data.policyHolder.streetType,
          roadNumber: data.policyHolder.house
        }),
        userDetails: new UserDetails({
          birthDate: data.policyHolder.birthDate
            ? CommonMapper.fromMiddlewareDate(data.policyHolder.birthDate)
            : undefined,
          name: data.policyHolder.name?.toUpperCase(),
          surname1: data.policyHolder.firstSurname?.toUpperCase(),
          surname2: data.policyHolder.secondSurname?.toUpperCase(),
          gender: data.policyHolder.sex,
          email: data.policyHolder.email,
          prefix: data.policyHolder.prefix,
          phone: data.policyHolder.mobilePhone ? String(data.policyHolder.mobilePhone) : undefined
        }),
        privacyPolicyConsent: data.policyHolder?.dataProtection?.privacyPolicyConsent,
        thirdPartyTelephoneAdvertising:
          data.policyHolder.dataProtection?.thirdPartyTelephoneAdvertising,
        thirdPartyWrittenAdvertising:
          data.policyHolder.dataProtection?.thirdPartyWrittenAdvertising,
        asisaNewsConsent: data.policyHolder.dataProtection?.asisaNewsConsent,
        dataTransfer: data.policyHolder.dataProtection?.dataTransfer
        // preContractualDocumentChecked:
        //   data.hiring.stepCode > PetsInsuranceProcessStepEnum.POLICY_HOLDER_SECOND in case we need to autocalculate this
      })
    })
  }

  static toRequest() {}
}
